import { Inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Idle, DEFAULT_INTERRUPTSOURCES, AutoResume } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { MatIconRegistry } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ADPResourceService } from 'digital-core';
import { SessionTimeoutModalWindowComponent } from 'src/app/shared/components/session-timeout-modal-window/session-timeout-modal-window.component';
import OktaAuth from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AppConstants } from '../constants';
import { PreInitSettingsService } from './settings/pre-init-setting.service';
import { PGHIdentityService } from './pghidentity.service';
import { firstValueFrom, Subject, timer, from, of } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class SessionManagementService implements OnDestroy {
  idleState: string;
  dialogRef: any;
  public isLogout = false;
  modalData: any = {};
  isActive = false;

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    public resourceService: ADPResourceService,
    public domSanitizer: DomSanitizer,
    public matIconRegistry: MatIconRegistry,
    private idle: Idle,
    private keepalive: Keepalive,
    private matDialog: MatDialog,
    private router: Router,
    private http: HttpClient,
    private preInitSettingsService: PreInitSettingsService,
    private pghIdentityService: PGHIdentityService
  ) {
  }


  configureSessionTimeOut() {
    this.idle.setTimeout(
      Number(
       this.resourceService.getPortalSetting('Session_WarningTimeOut').Value
      )
    );

    this.idle.setIdle(
      Number(this.resourceService.getPortalSetting('Session_IdleTime').Value)
    );

    // setting the events that will wake up the aplication from iddle
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.setAutoResume(AutoResume.notIdle);

    // notify if the user is back from idle
    this.idle.onIdleEnd.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.idleState = 'No longer idle.';
      if (this.dialogRef) {
        this.matDialog.closeAll();
      }
    });

    // notify if the session expires
    this.idle.onTimeout.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.dialogRef) {
        this.matDialog.closeAll();
      }
      this.idleState = 'Timed out!';
      this.logOff();
    });

    // will notify once the user is iddle
    this.idle.onIdleStart.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setWarning();
    });

    // showing the warning dialog if the session is about to expire.
    this.idle.onTimeoutWarning.pipe(takeUntil(this.destroy$)).subscribe(countdown => {
      this.increaseWarningCountDown(countdown);
    });

    // keeping alive the token
    /* Not used for now
    this.keepalive.interval(
      Number(
        //this.resourceService.getPortalSetting('Session_TokenRefreshInterval').Value
      )
    );
    this.keepalive.onPing.subscribe(async () => {
      }
    );
    */
    this.resetIdleState();
    this.startIdentityTokenRefresh();
  }

  resetIdleState() {
    this.idle.watch();
    this.idleState = 'Started.';
    // this.timedOut = false;
  }
  increaseWarningCountDown(count: number) {
    const body = (this.modalData.Body = this.resourceService.getViewLabel(
      'Global_Timeout_Body'
    ));

    this.modalData.body = body.replace('{Key_Seconds}', count.toString());
  }

  setWarning() {
    if(!this.isActive) {
        if(localStorage.getItem('okta-user') === null && localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityUser) === null) {
          return;
        }
        this.isActive = true;
        this.modalData.title = 'Global_Timeout_Title';
        this.modalData.body = this.resourceService.getViewLabel(
          'Global_Timeout_Body'
        ).replace('{Key_Seconds}', this.resourceService.getPortalSetting('Session_WarningTimeOut').Value);

        this.modalData.buttonContinueText = 'Global_ContinueSession';
        this.modalData.buttonEndText = 'Global_EndSession';

        this.dialogRef = this.matDialog.open(SessionTimeoutModalWindowComponent, {
          data: this.modalData
        });

        this.dialogRef.afterClosed().pipe(take(1)).subscribe(async result => {
          this.isActive = false;
          if (result && result.toLowerCase() === 'yes') {
            this.matDialog.closeAll();
            this.logOff();
          } else {
            this.idle.watch();
            this.dialogRef.close();
            this.matDialog.closeAll();
            await this.renewTokens();
          }
        });
    }
  }

  private async renewTokens(): Promise<void> {
    if(localStorage.getItem('okta-user') !== null) {
      const renewToken = await this.oktaAuth.token.renewTokens();
      await this.oktaAuth.tokenManager.setTokens(renewToken);
    } else if (this.pghIdentityService.isTokenValid()) {
      await this.renewIdentityToken();
    }
  }

  private startIdentityTokenRefresh(): void {
    const intervalMs = Number(this.resourceService.getPortalSetting('Session_IdentityToken_RefreshInterval')?.Value ?? 10) * 60 * 1000;
    timer(intervalMs, intervalMs)
    .pipe(
      takeUntil(this.destroy$),
      filter(() => !!localStorage.getItem(AppConstants.STORAGE_KEYS.IdentityToken)),
      switchMap(() => from(this.renewIdentityToken()).pipe(catchError(() => of(null))))
    )
    .subscribe();
  }

  private async renewIdentityToken(): Promise<void> {
    const isIdentityTokenValid = this.pghIdentityService.isTokenValid();
    const middlewareUrl = this.preInitSettingsService.getSettings()?.middleWare?.middlewareUrl;
    const accountPath = this.preInitSettingsService.getSettings()?.middleWare?.accountPath;
    const url = `${middlewareUrl}${accountPath}${AppConstants.API_ENDPOINTS.RenewIdentityToken}`;
    const request$ = this.http.post(url, null, { observe: 'response', headers: { lazy: '' } });
    const response = await firstValueFrom(
      isIdentityTokenValid
        ? request$
        : request$.pipe(catchError(() => of(null)))
    );
    if (!response) { return; }
    const renewedToken = response.headers.get(AppConstants.HEADERS.IdentityToken);
    if (renewedToken) {
      localStorage.setItem(AppConstants.STORAGE_KEYS.IdentityToken, renewedToken);
    }
    if (isIdentityTokenValid) {
      this.pghIdentityService.processIdentityToken();
    }
  }

  logOff() {
    this.router.navigate(['/logout']);
  }
}
